import { useState, useEffect } from 'react';
import { esopApi } from '../services/api';
import UploadESOPModal from './UploadESOPModal';
import GenerateESOPModal from './GenerateESOPModal';
import '../styles/Panel.css';

function ESOPPanel({ entity, onComplete }) {
  const [outputs, setOutputs] = useState([]);
  const [inputStatus, setInputStatus] = useState(null);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  useEffect(() => {
    if (entity) {
      fetchData();
    }
  }, [entity]);

  const fetchData = async () => {
    try {
      // Fetch outputs
      const outputsData = await esopApi.listOutputs(entity.name);
      setOutputs(outputsData.outputs || []);

      // Fetch input status
      const statusData = await esopApi.getStatus(entity.name);
      setInputStatus(statusData);
    } catch (err) {
      console.error('Failed to fetch ESOP data:', err);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setError(null);
      const blob = await esopApi.downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `esop_template_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download template: ' + err.message);
    }
  };

  const handleDownloadOutput = async (output) => {
    try {
      setError(null);
      const blob = await esopApi.downloadOutput(
        entity.name,
        output.period,
        output.timestamp
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `esop_workings_${entity.name}_${output.period}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download output: ' + err.message);
    }
  };

  const handleUploadComplete = () => {
    setShowUploadModal(false);
    fetchData();
    if (onComplete) onComplete();
  };

  const handleGenerateComplete = () => {
    setShowGenerateModal(false);
    fetchData();
    if (onComplete) onComplete();
  };

  return (
    <div className="panel esop-panel">
      <h2>ESOP (Employee Stock Option Plan) Workings</h2>
      <p className="panel-description">
        Generate comprehensive ESOP expense calculations with vesting schedules,
        fair value allocations, and journal entries
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Step 1: Download Template */}
      <div className="section">
        <h3>Step 1: Download & Fill ESOP Template</h3>
        <div className="info-box">
          <p>The template includes three sheets:</p>
          <ul>
            <li><strong>Grant Master Data:</strong> Employee grants with vesting schedules</li>
            <li><strong>Valuation Parameters:</strong> Fair value per option at grant dates</li>
            <li><strong>Company NAV Data:</strong> Optional NAV-based valuation data</li>
          </ul>
        </div>
        <div className="button-group">
          <button
            className="btn btn-secondary"
            onClick={handleDownloadTemplate}
          >
            📥 Download ESOP Template
          </button>
        </div>
      </div>

      {/* Step 2: Upload Data */}
      <div className="section">
        <h3>Step 2: Upload Filled ESOP Data</h3>
        {inputStatus?.has_data && (
          <div className="alert alert-success">
            ✓ Data uploaded on {new Date(inputStatus.latest_version.upload_time).toLocaleString()}
            <div className="status-details">
              <span>• {inputStatus.latest_version.grant_count || 0} grants</span>
              <span>• {inputStatus.latest_version.valuation_count || 0} valuations</span>
              <span>• {inputStatus.latest_version.nav_count || 0} NAV records</span>
            </div>
          </div>
        )}
        {!inputStatus?.has_data && (
          <div className="alert alert-warning">
            ⚠ No ESOP data uploaded yet. Please upload the filled template.
          </div>
        )}
        <div className="button-group">
          <button
            className="btn btn-primary"
            onClick={() => setShowUploadModal(true)}
          >
            📤 Upload ESOP Data
          </button>
        </div>
      </div>

      {/* Step 3: Generate Workings */}
      <div className="section">
        <h3>Step 3: Generate ESOP Workings</h3>
        <p>
          Generate comprehensive ESOP expense calculations including:
        </p>
        <ul>
          <li>Grant Chart with vesting matrix</li>
          <li>Expense summary by grant date</li>
          <li>Period-wise expense allocation</li>
          <li>Journal entry schedule</li>
        </ul>
        <div className="button-group">
          <button
            className="btn btn-primary"
            onClick={() => setShowGenerateModal(true)}
            disabled={!inputStatus?.has_data}
          >
            🔄 Generate ESOP Workings
          </button>
        </div>
      </div>

      {/* Generated Reports */}
      {outputs.length > 0 && (
        <div className="section">
          <h3>Generated ESOP Reports</h3>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Generated On</th>
                  <th>Timestamp</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {outputs.map((output, idx) => (
                  <tr key={idx}>
                    <td>{output.period}</td>
                    <td>
                      {output.generation_time
                        ? new Date(output.generation_time).toLocaleString()
                        : '-'}
                    </td>
                    <td><code>{output.timestamp}</code></td>
                    <td>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleDownloadOutput(output)}
                      >
                        📥 Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Feature Highlights */}
      <div className="section feature-highlights">
        <h3>Key Features</h3>
        <div className="feature-grid">
          <div className="feature-card">
            <div className="feature-icon">📊</div>
            <h4>Vesting Schedules</h4>
            <p>Automatic generation of vesting tranches with dates and share allocation</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">💰</div>
            <h4>Fair Value Allocation</h4>
            <p>Expense calculation using grant date fair values or NAV-based valuation</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">📅</div>
            <h4>Period-wise Expense</h4>
            <p>Pro-rata expense allocation across fiscal years with straight-line recognition</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">📝</div>
            <h4>Journal Entries</h4>
            <p>Ready-to-use journal entry schedule for each fiscal period</p>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showUploadModal && (
        <UploadESOPModal
          entity={entity}
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleUploadComplete}
        />
      )}

      {showGenerateModal && (
        <GenerateESOPModal
          entity={entity}
          onClose={() => setShowGenerateModal(false)}
          onSuccess={handleGenerateComplete}
        />
      )}
    </div>
  );
}

export default ESOPPanel;
